import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { deriveListKey, sanitizeKeyInput, LIST_KEY_MAX } from '../lib/itemId';

interface CreateListDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: { name: string; description?: string; itemKey?: string }) => void;
}

export function CreateListDialog({ open, onClose, onCreate }: CreateListDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // The key is auto-suggested from the name until the user edits it, after
  // which we stop overwriting their choice.
  const [itemKey, setItemKey] = useState('');
  const [keyEdited, setKeyEdited] = useState(false);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  function handleNameChange(value: string) {
    setName(value);
    if (!keyEdited) setItemKey(value.trim() ? deriveListKey(value) : '');
  }

  function resetForm() {
    setName('');
    setDescription('');
    setItemKey('');
    setKeyEdited(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    onCreate({
      name: trimmedName,
      description: description.trim() || undefined,
      // Fall back to server-side derivation if somehow empty.
      itemKey: itemKey || undefined,
    });

    resetForm();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Todo List</DialogTitle>
          <DialogDescription>Create a new list to organize your tasks.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="list-name">Name</Label>
            <Input
              id="list-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="My Todo List"
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="list-key">Task ID prefix</Label>
            <Input
              id="list-key"
              value={itemKey}
              onChange={(e) => {
                setItemKey(sanitizeKeyInput(e.target.value));
                setKeyEdited(true);
              }}
              placeholder="FR"
              maxLength={LIST_KEY_MAX}
              className="font-mono uppercase w-28"
            />
            <p className="text-xs text-muted-foreground">
              Tasks get ids like{' '}
              <code className="rounded bg-muted px-1 py-0.5">{(itemKey || 'FR') + '-1'}</code>. You
              can change it later.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="list-description">Description (optional)</Label>
            <Textarea
              id="list-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this list for?"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
